import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import iwalkcvService from '../../services/iwalkcv.service';

interface Avaliacao {
  id: number;
  exp_titulo: string;
  nome_empresa: string;
  rating: number;
  comentario?: string;
  status: 'PENDING' | 'PUBLISHED' | 'REJECTED';
  created_at: string;
}

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning',
  PUBLISHED: 'success',
  REJECTED: 'error',
};

const renderStars = (rating: number): string => {
  const full = Math.round(rating);
  return '★'.repeat(full) + '☆'.repeat(5 - full);
};

const truncate = (str: string | undefined, max: number): string => {
  if (!str) return '—';
  return str.length > max ? str.slice(0, max) + '…' : str;
};

const IWalkCVAvaliacoes: React.FC = () => {
  const [avaliacoes, setAvaliacoes] = useState<Avaliacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await iwalkcvService.getAvaliacoes({
        status: statusFilter || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setAvaliacoes(res.data || res.avaliacoes || res);
      setTotal(res.total || res.pagination?.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, page, rowsPerPage]);

  const handleFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleSetStatus = async (id: number, status: 'PUBLISHED' | 'REJECTED') => {
    setActionLoading(id);
    try {
      await iwalkcvService.setAvaliacaoStatus(id, status);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700}>Avaliações</Typography>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            value={statusFilter}
            label="Estado"
            onChange={(e) => handleFilterChange(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="PENDING">Pendente</MenuItem>
            <MenuItem value="PUBLISHED">Publicado</MenuItem>
            <MenuItem value="REJECTED">Rejeitado</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Experiência</TableCell>
              <TableCell>Operador</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Comentário</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="right">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : avaliacoes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma avaliação encontrada.
                </TableCell>
              </TableRow>
            ) : (
              avaliacoes.map((av) => (
                <TableRow key={av.id} hover>
                  <TableCell>{av.id}</TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>{av.exp_titulo}</TableCell>
                  <TableCell>{av.nome_empresa}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', color: 'warning.main', whiteSpace: 'nowrap' }}>
                    {renderStars(av.rating)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, color: 'text.secondary', fontSize: 12 }}>
                    {truncate(av.comentario, 100)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={av.status}
                      color={STATUS_COLORS[av.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(av.created_at).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {actionLoading === av.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <>
                        <Tooltip title="Publicar">
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: 'success.main' }}
                              disabled={av.status === 'PUBLISHED'}
                              onClick={() => handleSetStatus(av.id, 'PUBLISHED')}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Rejeitar">
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: 'error.main' }}
                              disabled={av.status === 'REJECTED'}
                              onClick={() => handleSetStatus(av.id, 'REJECTED')}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Por página:"
        />
      </TableContainer>
    </Box>
  );
};

export default IWalkCVAvaliacoes;
